// The shape model (ADR-0023 Amendment 1, board 25 v2 §ج): which of the six
// booking shapes a category books on, what its fifth step is, what its sixth
// step is called, and the scheduling PUT that enrols the business on it.
// Pure — no React, no fetch. The step map (steps.ts), the type sentence
// (Business.tsx) and the one writer (enroll.ts) all read it.

import type { TermProfile } from '@/lib/terminology';
import { SHAPE_COPY } from '../copy';
import type { BookingShape, BusinessType } from './api';

export type { BookingShape } from './api';

const SIX: ReadonlySet<string> = new Set<BookingShape>(['staff', 'place', 'business', 'unit', 'seat', 'table']);

/** The legacy scheduling mode → its shape (a category profile has not tagged yet). */
const MODE_SHAPE: Record<string, BookingShape> = { per_staff: 'staff', per_service: 'place', shared_business: 'business' };

/** The shape's legacy mode: booking derives the mode from staff / place / business anyway. */
const SHAPE_MODE: Record<BookingShape, string> = {
  staff: 'per_staff',
  place: 'per_service',
  business: 'shared_business',
  unit: 'shared_business',
  seat: 'shared_business',
  table: 'shared_business',
};

/** booking_shape when it is one of the six; else the legacy mode's shape; else null (say nothing). */
export function shapeOf(t: Pick<BusinessType, 'booking_shape' | 'default_scheduling_mode'> | null | undefined): BookingShape | null {
  if (!t) return null;
  if (t.booking_shape && SIX.has(t.booking_shape)) return t.booking_shape;
  return MODE_SHAPE[t.default_scheduling_mode ?? ''] ?? null;
}

/** Step 5: the weekly hours, the work days (no times), the handover times, or no step (seats). */
export type Step5 = 'hours' | 'workdays' | 'handover' | 'none';

export interface ShapeSpec {
  shape: BookingShape;
  /** The type-step sentence word («الحجز في هذه الفئة يكون على {word}»). */
  word: string;
  /** The shape step's counter label («الفريق», «الملاعب», «الفترات» …). */
  stepLabel: (t: TermProfile, category: string) => string;
  step5: Step5;
  /** The shape step's «إنهاء الإعداد» needs one record (shapes 2–6, ruling R8). */
  minOne: boolean;
  /** «تخطّي الآن» — shape 1 only. */
  skippable: boolean;
}

function spec(shape: BookingShape, step5: Step5): ShapeSpec {
  const c = SHAPE_COPY[shape];
  return {
    shape,
    word: c.word,
    stepLabel: (_t, category) => c.label(category),
    step5,
    minOne: shape !== 'staff',
    skippable: shape === 'staff',
  };
}

export const SHAPES: Record<BookingShape, ShapeSpec> = {
  staff: spec('staff', 'hours'),
  place: spec('place', 'hours'),
  business: spec('business', 'workdays'),
  unit: spec('unit', 'handover'),
  seat: spec('seat', 'none'),
  table: spec('table', 'hours'),
};

export interface SchedulingExtras {
  checkInMin?: number;
  checkOutMin?: number;
  sittingMin?: number;
  tableDeposit?: { enabled: boolean; kind?: 'per_person' | 'per_table'; value?: number };
}

/** The booking PUT /v1/business/{id}/scheduling body (ihjizli-booking schedulingReq). */
export interface SchedulingPut {
  mode: string;
  slot_step_min: 30;
  allow_staff_pick: true;
  shape: BookingShape;
  check_in_min?: number;
  check_out_min?: number;
  sitting_min?: number;
  table_deposit_enabled?: boolean;
  table_deposit_kind?: string;
  table_deposit_value?: number;
}

const VALID_MODES: ReadonlySet<string> = new Set(['per_staff', 'per_service', 'shared_business']);

/**
 * The scheduling PUT for a shape: the category's default mode when valid (else
 * the shape's legacy mode), the shape itself, and the shape's own extras —
 * unit the check-in/out minutes, table the sitting minutes and the deposit.
 * Extras of other shapes are not sent.
 */
export function schedulingBody(shape: BookingShape, type: Pick<BusinessType, 'default_scheduling_mode'> | null | undefined, extras: SchedulingExtras): SchedulingPut {
  const m = type?.default_scheduling_mode;
  const body: SchedulingPut = { mode: m && VALID_MODES.has(m) ? m : SHAPE_MODE[shape], slot_step_min: 30, allow_staff_pick: true, shape };
  if (shape === 'unit') {
    if (extras.checkInMin != null) body.check_in_min = extras.checkInMin;
    if (extras.checkOutMin != null) body.check_out_min = extras.checkOutMin;
  }
  if (shape === 'table') {
    if (extras.sittingMin != null) body.sitting_min = extras.sittingMin;
    const d = extras.tableDeposit;
    if (d) {
      body.table_deposit_enabled = d.enabled;
      if (d.enabled) {
        if (d.kind) body.table_deposit_kind = d.kind;
        if (d.value != null) body.table_deposit_value = d.value;
      }
    }
  }
  return body;
}
